export type HudMessagePriority = 'timing' | 'status' | 'error';

export interface HudMessage {
  text: string;
  accessibleText: string;
  className: string;
  priority: HudMessagePriority;
}

const PRIORITY: Record<HudMessagePriority, number> = {
  status: 1,
  timing: 2,
  error: 3,
};

/**
 * Pure message ordering for the HUD. Informational messages wait their turn;
 * an explicit error may interrupt, but the interrupted message is retained so
 * it can be displayed after the error expires.
 */
export class HudMessageQueue {
  private activeMessage: HudMessage | null = null;
  private readonly pendingMessages: Array<{ message: HudMessage; sequence: number }> = [];
  private nextSequence = 0;

  enqueue(message: HudMessage): HudMessage | null {
    if (!this.activeMessage) {
      this.activeMessage = message;
      return message;
    }
    if (PRIORITY[message.priority] > PRIORITY[this.activeMessage.priority]) {
      this.pendingMessages.unshift({ message: this.activeMessage, sequence: this.nextSequence++ });
      this.activeMessage = message;
      return message;
    }
    this.pendingMessages.push({ message, sequence: this.nextSequence++ });
    this.pendingMessages.sort(
      (left, right) =>
        PRIORITY[right.message.priority] - PRIORITY[left.message.priority] ||
        left.sequence - right.sequence,
    );
    return null;
  }

  complete(): HudMessage | null {
    const next = this.pendingMessages.shift()?.message ?? null;
    this.activeMessage = next;
    return next;
  }

  getActive(): HudMessage | null {
    return this.activeMessage;
  }

  clear(): void {
    this.activeMessage = null;
    this.pendingMessages.length = 0;
  }
}
